import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { ArabicNumberPipe } from 'src/app/shared/pipes/arabic-number.pipe';
import { DataService } from 'src/app/dashboard/_services/data.service';
import { StaffService } from '../../school-management/_services/staff.service';
import { ControlActivationService } from 'src/app/_services/control-activation.service';
import { GradeService } from '../_services/grades.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GlobalService } from 'src/app/_services/global.service';
import { TranslateService } from '@ngx-translate/core';
import { ErrorMsgService } from 'src/app/shared/error-msg/error-msg.service';
import { CountryService } from 'src/app/_services/country.service';
import { MatDialog } from '@angular/material/dialog';
import { PermissionDeniedDialogComponent } from 'src/app/shared/permissions/permission-denied-dialog.component';
import { Role, canManageStaffRole } from 'src/app/shared/permissions/permissions';
import { HttpErrorResponse } from '@angular/common/http';
import { combineLatest, Observable, Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import {
  NationalIdChoice,
  NationalIdChoiceDialogData
} from './national-id-choice-dialog/national-id-choice-dialog.component';
import {
  GradeWithClasses, LinkChip, LinkKind, SchoolClass, classChips, filterByName, filterGradeTree,
  gradeCheckState, gradeChips, linkKindFor, pluralCategory, sameIds
} from './staff-links';

declare var $: any;

// what the person is linked to, for one role
interface LinkSnapshot {
  role: string;
  classIds: number[];
  gradeIds: number[];
  gateId: number | null;
}

// Edit: the role changed and the old role's links will be removed on Save (AC 15)
interface RoleChangeNotice {
  undo: LinkSnapshot;
  kind: LinkKind;
  count: number;
}

// links failed after the person saved: 'failed' can be retried, 'blocked' (HTTP 400, active delegation) can't
type LinksError = 'failed' | 'blocked';

@Component({
  selector: 'app-add-staff',
  templateUrl: './add-staff.component.html',
  styleUrls: ['./add-staff.component.scss']
})
export class AddStaffComponent implements OnInit, OnDestroy {
  @ViewChild('rolePane') rolePane: ElementRef<HTMLElement>;
  // the modal's own file input — never look it up by id, other components on the
  // same page use the same id and getElementById would hand us theirs
  @ViewChild('fileUploadInput') fileUploadInput: ElementRef<HTMLInputElement>;
  imageFile;
  selectedImage;
  image;
  clazz;
  addStaffForm: UntypedFormGroup;
  showedNumber: number;
  completedProfile: boolean;
  staffActivation: boolean;
  staffID: number;
  staffName: string;
  modalTitle: string;
  formFlag: string;
  oldRoleType: string;
  roleType: string;
  roles = ['school_admin', 'teacher', 'supervisor', 'security', 'shared_gate_scanner', 'gate', 'manager' , 'cafeteria'];//,'cafeteria'
  earlyDismissalFlag: boolean;
  receiveBusRequestsFlag: boolean;
  enableObserverModeFlag: boolean;
  dailyReportsFlag: boolean;
  weeklyReportsFlag: boolean;
  currLang;
  countries = [];
  // true while add/update or the links are in flight, so a double click can't send twice
  saving = false;
  // set while the B1 National ID choice is open (rendered inside #addStaffModal)
  nationalIdChoice: NationalIdChoiceDialogData = null;

  // role pane
  grades: GradeWithClasses[] = [];
  gates: { id: number; name: string }[] = [];
  linkSearch = '';
  expandedGradeIds = new Set<number>();
  selectedClassIds = new Set<number>();
  selectedGradeIds = new Set<number>();
  selectedGateId: number | null = null;
  chips: LinkChip[] = [];
  // what the server has for this person; links are sent only when they differ or the role changed (AC 11)
  private savedLinks: LinkSnapshot = this.emptyLinks('');
  // Save was pressed once with nothing linked (AC 7)
  reminderShown = false;
  roleChangeNotice: RoleChangeNotice = null;
  linksError: LinksError = null;
  // the role before the last change, for Undo
  private previousRole: string;
  private destroy$ = new Subject<void>();
  // ends the subscriptions of one opening of the dialog
  private closed$ = new Subject<void>();

  constructor(
    private dataService: DataService,
    private staffService: StaffService,
    private controlActivation: ControlActivationService,
    private _snackBar: MatSnackBar,
    private globalService: GlobalService,
    private translate: TranslateService,
    private errorMsgService: ErrorMsgService,
    private gradesService: GradeService,
    private countryService: CountryService,
    private dialog: MatDialog,
    private router: Router) {
  }

  ngOnInit(): void {
    // to remove the black screen and the stuck error msg popup if error message was just closed in sibling component
    $('.modal').modal('hide');
    $('body').removeClass('modal-open');
    $('.modal-backdrop').remove();
    this.globalService.getLang().pipe(takeUntil(this.destroy$)).subscribe(lng => {
      this.currLang = lng;
      this.translate.use(this.currLang);
    });
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.setModalTitle());

    $(document).on('hidden.bs.modal', '#addStaffModal', () => {
      this.closeModalHandler();
    });
    $(document).on('shown.bs.modal', '#addStaffModal', () => {
      this.onOpened();
    });

    this.roleType = null;
    this.addStaffForm = new UntypedFormGroup({
      name: new UntypedFormControl('', [Validators.required]),
      national_id: new UntypedFormControl('', [Validators.required, Validators.pattern('[a-zA-Z0-9]*')]),
      role_type: new UntypedFormControl('', Validators.required),
      country_code: new UntypedFormControl('', Validators.required),
      phone: new UntypedFormControl('', [Validators.required, Validators.pattern('[0-9]*')]),
      email: new UntypedFormControl('', [Validators.email, Validators.required, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$')]),
      gender: new UntypedFormControl(''),
    });

    this.dataService.getRowsNumberShown().pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      this.showedNumber = res;
    });

    this.gradesService.getClassData().pipe(takeUntil(this.destroy$)).subscribe(res => {
      if (res) {
        this.clazz = res;
      }
    });
  }

  private onOpened() {
    this.countries = this.countryService.countries;
    this.loadLinkOptions();
    // the latest staff data only: subscribing on every opening must not stack listeners
    this.staffService.getStaffData().pipe(take(1)).subscribe((res: any) => {
      this.setUpFromStaffData(res);
      this.filterCafeteriaInRolesArray();
      if (res?.focus === 'links') {
        this.focusRolePane();
      }
    });
  }

  private setUpFromStaffData(res: any) {
    const flag = res?.flag === 'edit' || res?.flag === 'view' ? res.flag : 'add';
    const staff = flag === 'add' ? null : res?.staff;
    this.formFlag = flag;
    this.setModalTitle();
    this.resetLinkState();
    this.roleType = this.addStaffForm.value.role_type || null;
    this.previousRole = this.roleType;

    const school = staff?.schools?.find(item => item.id == localStorage.getItem('schoolId'));
    this.earlyDismissalFlag = school?.pivot?.receive_early_dismissal_requests == 1;
    this.receiveBusRequestsFlag = school?.pivot?.receive_bus_requests == 1;
    this.enableObserverModeFlag = school?.pivot?.observer_mode == 1;
    this.dailyReportsFlag = school?.pivot?.receive_daily_report == 1;
    this.weeklyReportsFlag = school?.pivot?.receive_weekly_report == 1;
    if (!staff) {
      return;
    }

    this.image = environment.img_url + staff.image_path;
    // to fix double role issue role doesn't appear in the modal if first role not a staff member
    const roles = (staff.roles || []).filter(role => role.name != 'parent' && role.name != 'helper');
    this.completedProfile = staff.is_completed_profile === 1;
    this.staffID = staff.id;
    this.staffName = staff.name;
    if (staff.schools?.length > 0) {
      this.staffActivation = staff.schools[0].pivot.is_active === 1; // add schools object
    }
    this.addStaffForm.patchValue({
      name: staff.name,
      national_id: staff.national_id,
      role_type: roles[0]?.name,
      gender: staff.gender,
    });
    if (staff.schools?.length > 0) {
      this.addStaffForm.patchValue({
        country_code: staff.country_code,
        phone: staff.phone,
        email: staff.email,
      });
    }
    this.roleType = this.addStaffForm.value['role_type'];
    this.oldRoleType = this.roleType;
    this.previousRole = this.roleType;
    this.savedLinks = this.linksFromStaff(staff, this.roleType);
    this.applyLinks(this.savedLinks);
  }

  // image upload
  changeStaffProfile() {
    const fileUpload = this.fileUploadInput?.nativeElement;
    if (!fileUpload) {
      return;
    }
    // clearing the value first so re-picking the same file still fires `change`
    fileUpload.value = null;
    fileUpload.click();
  }

  onFileSelected(file: FileList) {
    const picked = file?.item(0);
    if (!picked) {
      return;
    }
    // the upload payload and the preview are set together, so they can never diverge
    this.imageFile = picked;
    this.selectedImage = picked;
    const reader = new FileReader();
    reader.onload = (event: any) => {
      this.image = event.target.result;
    };
    reader.readAsDataURL(picked);
  }

  private linksFromStaff(staff: any, role: string): LinkSnapshot {
    const links = this.emptyLinks(role);
    switch (linkKindFor(role)) {
      case 'classes':
        links.classIds = (staff?.classes || []).map(clazz => clazz.id);
        break;
      case 'grades':
        links.gradeIds = (staff?.grades || []).map(grade => grade.id);
        break;
      case 'gate':
        links.gateId = staff?.gates?.[0]?.id ?? null;
        break;
    }
    return links;
  }

  private emptyLinks(role: string): LinkSnapshot {
    return { role, classIds: [], gradeIds: [], gateId: null };
  }

  private currentLinks(): LinkSnapshot {
    return {
      role: this.roleType,
      classIds: Array.from(this.selectedClassIds),
      gradeIds: Array.from(this.selectedGradeIds),
      gateId: this.selectedGateId,
    };
  }

  private applyLinks(links: LinkSnapshot) {
    this.selectedClassIds = new Set(links.classIds);
    this.selectedGradeIds = new Set(links.gradeIds);
    this.selectedGateId = links.gateId;
    this.expandFirstPickedGrade();
    this.onLinksChanged();
  }

  // grades (with their classes) and gates come from the app cache, as before; no request on open
  private loadLinkOptions() {
    combineLatest([this.dataService.setAllGrades, this.dataService.setAllGates]).pipe(
      takeUntil(this.closed$)
    ).subscribe(([grades, gates]) => {
      this.grades = Array.isArray(grades) ? grades : [];
      this.gates = Array.isArray(gates) ? gates : [];
      this.expandFirstPickedGrade();
      this.refreshChips();
    });
  }

  // skeleton only while the cache has never been filled
  get linksLoading(): boolean {
    return this.linkKind === 'gate' ? this.dataService.allGates == null : this.dataService.allGrades == null;
  }

  private resetLinkState() {
    this.linkSearch = '';
    this.expandedGradeIds = new Set();
    this.selectedClassIds = new Set();
    this.selectedGradeIds = new Set();
    this.selectedGateId = null;
    this.savedLinks = this.emptyLinks('');
    this.reminderShown = false;
    this.roleChangeNotice = null;
    this.linksError = null;
    this.chips = [];
  }

  private setModalTitle() {
    this.modalTitle = this.translate.instant(this.formFlag === 'add' ? 'add-staff.add' : 'add-staff.staff-info');
  }

  // the "Assign class / Assign gate" quick action opens Edit with the role pane in focus
  private focusRolePane() {
    setTimeout(() => {
      const pane = this.rolePane?.nativeElement;
      pane?.scrollIntoView({ block: 'nearest' });
      const target = pane?.querySelector<HTMLElement>('input, button');
      target?.focus();
    });
  }

  // ---- role pane ------------------------------------------------------------------------------

  get linkKind(): LinkKind | null {
    return linkKindFor(this.roleType);
  }

  // any picked role that links nothing and has no report switches
  get hasNothingToSetUp(): boolean {
    return !!this.roleType && !this.linkKind && this.roleType !== 'school_admin' && this.roleType !== 'manager';
  }

  get isReadonly(): boolean {
    return this.formFlag === 'view';
  }

  // teacher: grades that have classes; viewer: every grade
  get gradesForPane(): GradeWithClasses[] {
    return this.linkKind === 'classes'
      ? this.grades.filter(grade => grade.school_classes?.length > 0)
      : this.grades;
  }

  get visibleGradeTree(): GradeWithClasses[] {
    return filterGradeTree(this.gradesForPane, this.linkSearch);
  }

  get visibleGrades(): GradeWithClasses[] {
    return filterByName(this.gradesForPane, this.linkSearch);
  }

  get visibleGates() {
    return filterByName(this.gates, this.linkSearch);
  }

  get hasAnyLink(): boolean {
    switch (this.linkKind) {
      case 'classes': return this.selectedClassIds.size > 0;
      case 'grades': return this.selectedGradeIds.size > 0;
      case 'gate': return this.selectedGateId != null;
      default: return true;
    }
  }

  isGradeExpanded(grade: GradeWithClasses): boolean {
    return !!this.linkSearch.trim() || this.expandedGradeIds.has(grade.id);
  }

  toggleGradeExpanded(grade: GradeWithClasses) {
    if (this.expandedGradeIds.has(grade.id)) {
      this.expandedGradeIds.delete(grade.id);
    } else {
      this.expandedGradeIds.add(grade.id);
    }
  }

  private expandFirstPickedGrade() {
    const first = this.gradesForPane.find(grade => gradeCheckState(grade, this.selectedClassIds) !== 'none');
    if (first) {
      this.expandedGradeIds.add(first.id);
    }
  }

  // state of the full grade, not the search-filtered copy
  gradeState(grade: GradeWithClasses) {
    const full = this.grades.find(item => item.id === grade.id) || grade;
    return gradeCheckState(full, this.selectedClassIds);
  }

  // ticking a grade picks all of its current classes; classes added later are not included (AC 3)
  toggleGradeClasses(grade: GradeWithClasses) {
    const full = this.grades.find(item => item.id === grade.id) || grade;
    const classIds = (full.school_classes || []).map(clazz => clazz.id);
    if (this.gradeState(full) === 'all') {
      classIds.forEach(id => this.selectedClassIds.delete(id));
    } else {
      classIds.forEach(id => this.selectedClassIds.add(id));
    }
    this.onLinksChanged();
  }

  toggleClass(clazz: SchoolClass) {
    this.toggleId(this.selectedClassIds, clazz.id);
    this.onLinksChanged();
  }

  toggleGrade(grade: GradeWithClasses) {
    this.toggleId(this.selectedGradeIds, grade.id);
    this.onLinksChanged();
  }

  selectGate(gateId: number) {
    this.selectedGateId = gateId;
    this.onLinksChanged();
  }

  // Select all / Clear act on what the search shows
  selectAllVisible() {
    if (this.linkKind === 'classes') {
      this.visibleGradeTree.forEach(grade => (grade.school_classes || []).forEach(clazz => this.selectedClassIds.add(clazz.id)));
    } else if (this.linkKind === 'grades') {
      this.visibleGrades.forEach(grade => this.selectedGradeIds.add(grade.id));
    }
    this.onLinksChanged();
  }

  clearVisible() {
    if (this.linkKind === 'classes') {
      this.visibleGradeTree.forEach(grade => (grade.school_classes || []).forEach(clazz => this.selectedClassIds.delete(clazz.id)));
    } else if (this.linkKind === 'grades') {
      this.visibleGrades.forEach(grade => this.selectedGradeIds.delete(grade.id));
    }
    this.onLinksChanged();
  }

  removeChip(chip: LinkChip) {
    const [type, rawId] = chip.key.split(':');
    const id = Number(rawId);
    if (type === 'class') {
      this.selectedClassIds.delete(id);
    } else if (type === 'grade' && this.linkKind === 'classes') {
      const grade = this.grades.find(item => item.id === id);
      (grade?.school_classes || []).forEach(clazz => this.selectedClassIds.delete(clazz.id));
    } else if (type === 'grade') {
      this.selectedGradeIds.delete(id);
    }
    this.onLinksChanged();
  }

  private toggleId(ids: Set<number>, id: number) {
    if (ids.has(id)) {
      ids.delete(id);
    } else {
      ids.add(id);
    }
  }

  private onLinksChanged() {
    // choosing anything removes the reminder and the button goes back to "Save" (AC 8)
    if (this.hasAnyLink) {
      this.reminderShown = false;
    }
    this.refreshChips();
  }

  private refreshChips() {
    switch (this.linkKind) {
      case 'classes':
        this.chips = classChips(this.gradesForPane, this.selectedClassIds);
        break;
      case 'grades':
        this.chips = gradeChips(this.grades, this.selectedGradeIds);
        break;
      default:
        this.chips = [];
    }
  }

  // counts use the app's digits: Arabic-Indic in Arabic (same as the arabicNumber pipe)
  private readonly arabicNumber = new ArabicNumberPipe();

  private localCount(count: number): string {
    return this.arabicNumber.transform(count, this.currLang);
  }

  moreChipsLabel = (hidden: number) =>
    this.translate.instant(`add-staff.links.more.${pluralCategory(this.currLang, hidden) === 'one' ? 'one' : 'other'}`,
      { count: this.localCount(hidden) })

  get saveLabelKey(): string {
    if (this.reminderShown && !this.hasAnyLink) {
      return `add-staff.links.save-without.${this.linkKind}`;
    }
    return 'add-staff.save';
  }

  get roleChangeText(): string {
    const notice = this.roleChangeNotice;
    if (!notice) {
      return '';
    }
    const category = pluralCategory(this.currLang, notice.count);
    const key = `add-staff.links.will-be-removed.${notice.kind}.${category}`;
    const count = this.localCount(notice.count);
    const text = this.translate.instant(key, { count });
    return text !== key
      ? text
      : this.translate.instant(`add-staff.links.will-be-removed.${notice.kind}.other`, { count });
  }

  goToGradesAndClasses() {
    $('#addStaffModal').modal('hide');
    this.router.navigate(['/school-management/grades-classes']);
  }

  // ---- role change ----------------------------------------------------------------------------

  getAssignPartState() {
    const newRole = this.addStaffForm.value['role_type'];
    const before = { ...this.currentLinks(), role: this.previousRole };
    const oldKind = linkKindFor(this.previousRole);
    const newKind = linkKindFor(newRole);
    this.roleType = newRole;
    this.linkSearch = '';
    this.reminderShown = false;

    if (newKind === oldKind) {
      // Security <-> Shared Gate keeps the gate
      this.applyLinks({ ...before, role: newRole });
    } else if (newRole === this.oldRoleType) {
      this.applyLinks(this.savedLinks);
    } else {
      this.applyLinks(this.emptyLinks(newRole));
    }
    this.roleChangeNotice = this.buildRoleChangeNotice(before);
    this.previousRole = newRole;
  }

  // Edit only: say what the saved role loses, with Undo, before Save (AC 15)
  private buildRoleChangeNotice(undo: LinkSnapshot): RoleChangeNotice {
    const savedKind = linkKindFor(this.oldRoleType);
    if (this.formFlag !== 'edit' || !savedKind || linkKindFor(this.roleType) === savedKind) {
      return null;
    }
    const count = savedKind === 'classes' ? this.savedLinks.classIds.length
      : savedKind === 'grades' ? this.savedLinks.gradeIds.length
        : (this.savedLinks.gateId != null ? 1 : 0);
    if (count === 0) {
      return null;
    }
    // keep the first Undo target while the role keeps changing, so Undo always goes back to the saved role's links
    const firstUndo = this.roleChangeNotice?.undo || undo;
    return { undo: firstUndo, kind: savedKind, count };
  }

  undoRoleChange() {
    const undo = this.roleChangeNotice?.undo;
    if (!undo) {
      return;
    }
    this.addStaffForm.controls['role_type'].setValue(undo.role);
    this.roleType = undo.role;
    this.previousRole = undo.role;
    this.roleChangeNotice = null;
    this.reminderShown = false;
    this.applyLinks(undo);
  }

  // ---- details --------------------------------------------------------------------------------

  getStaffFormValidation(control: string) {
    let errorMSG;
    // a rule refused by the server (BLU-47): show its EN/AR message as is
    const serverMessage = this.addStaffForm.controls[control].errors?.server;
    if (typeof serverMessage === 'string') {
      return serverMessage;
    }
    if (this.addStaffForm.controls[control].errors) {
      switch (control) {
        case 'name':
          errorMSG = this.addStaffForm.controls[control].errors.required ? this.translate.instant('add-staff.name-required')
            : this.addStaffForm.controls[control].errors.incorrect ? this.translate.instant('add-staff.name-exists')
              : this.translate.instant('add-staff.name-valid');

          break;
        case 'phone':
          errorMSG = this.addStaffForm.controls[control].errors.required ? this.translate.instant('add-staff.phone-required')
            : this.addStaffForm.controls[control].errors.incorrect ? this.translate.instant('add-staff.phone-exists')
              : this.translate.instant('add-staff.phone-valid');
          break;

        case 'national_id':
          errorMSG = this.addStaffForm.controls[control].errors.required ? this.translate.instant('add-staff.id-required')
            : this.addStaffForm.controls[control].errors.incorrect ? this.translate.instant('add-staff.id-exists')
              : this.translate.instant('add-staff.id-valid');
          break;

        case 'email':
          errorMSG = this.addStaffForm.controls[control].errors.required ? this.translate.instant('add-staff.mail-required')
            : this.addStaffForm.controls[control].errors.incorrect ? this.translate.instant('add-staff.mail-exists')
              : this.translate.instant('add-staff.mail-valid');
          break;
        default:
          break;
      }
    }
    return errorMSG;
  }

  // the logged-in user's role (this.roleType is overwritten with the edited staff's role)
  private get loggedInRole(): Role {
    return (localStorage.getItem('loginAs') || '').trim() as Role;
  }

  editStaff() {
    if (!canManageStaffRole(this.loggedInRole, this.oldRoleType)) {
      this.dialog.open(PermissionDeniedDialogComponent, { width: '400px' });
      return;
    }
    this.formFlag = 'edit';
  }

  changeActivation() {
    let body = { 'noOfShownRows': this.showedNumber, 'type': '', 'userName': this.staffName, 'userId': this.staffID, 'state': !this.staffActivation, 'event': '' }
    this.controlActivation.setToggledUSer(body);
    $("#confirmModal").modal("show");
  }


  getRoleName(roleName: string) {
    let translated = this.translate.instant(`roles.${roleName}`);
    if (translated !== `roles.${roleName}`) {
      return translated;
    }
    let newRoleName;
    if (this.currLang === 'en') {
      newRoleName = roleName.includes('_') ? roleName.replace(/_/g, " ") : roleName
    }
    else{
      newRoleName = this.translate.instant(`rolesِArr.${roleName}`);
    }

    return newRoleName;
  }

  closeModalHandler() {
    this.closed$.next();
    this.nationalIdChoice = null;
    this.dataService.setRowsNumberShown(this.showedNumber);
    this.completedProfile = false;
    this.image = null;
    this.imageFile = null;
    this.roleType = null;
    this.oldRoleType = undefined;
    this.previousRole = undefined;
    this.addStaffForm.reset();
    this.resetLinkState();
    $("#addStaffModal").modal("hide");
  }

  sanitizeDataBeforeSendToServer(): void {
    let staffGender = this.addStaffForm.get('gender');
    if ((staffGender.value as string)?.trim() === '' || staffGender.value == null || staffGender.value == 'noStaffGender') {
        delete this.addStaffForm.value['gender'];
    }
  }

  // ---- saving ---------------------------------------------------------------------------------

  saveStaff() {
    // the role dropdown hides School Admin, this is the backstop for the new role and the one being edited
    if (!canManageStaffRole(this.loggedInRole, this.addStaffForm.value['role_type'])
      || !canManageStaffRole(this.loggedInRole, this.oldRoleType)) {
      this.dialog.open(PermissionDeniedDialogComponent, { width: '400px' });
      return;
    }
    // a gentle reminder, never a block: the second press saves the account unlinked (AC 7)
    if (!this.hasAnyLink && !this.reminderShown) {
      this.reminderShown = true;
      return;
    }
    this.submitStaff();
  }

  // nationalIdChoice is sent only to answer a 409 NATIONAL_ID_CHOICE_REQUIRED (B1)
  private submitStaff(nationalIdChoice?: NationalIdChoice) {
    if (this.saving) {
      return;
    }
    this.sanitizeDataBeforeSendToServer();
    const formData = new FormData();
    for (const i in this.addStaffForm.value) {
      formData.append(i, this.addStaffForm.value[i])
    }
    if (this.imageFile) {
      formData.append('image', this.imageFile);
    }
    const requestSwitches = this.roleType == 'teacher' || this.roleType == 'supervisor';
    const reportSwitches = this.roleType == 'school_admin' || this.roleType == 'manager';
    // Add re-uses an existing account's school row (BLU-65): a flag left out keeps its old value there,
    // so Add always sends all five, 0 for an unticked switch or one the role does not show
    if (requestSwitches || this.formFlag === 'add') {
      formData.append('receive_early_dismissal_requests', requestSwitches && this.earlyDismissalFlag ? '1' : '0');
      formData.append('receive_bus_requests', requestSwitches && this.receiveBusRequestsFlag ? '1' : '0');
      formData.append('observer_mode', requestSwitches && this.enableObserverModeFlag ? '1' : '0');
    }
    if (reportSwitches || this.formFlag === 'add') {
      formData.append('receive_daily_report', reportSwitches && this.dailyReportsFlag ? '1' : '0');
      formData.append('receive_weekly_report', reportSwitches && this.weeklyReportsFlag ? '1' : '0');
    }
    this.saving = true;
    if (this.formFlag === 'add') {
      if (nationalIdChoice) {
        formData.append('national_id_choice', nationalIdChoice);
      }
      const staffName = this.addStaffForm.value.name;
      this.staffService.addStaff(formData).pipe(finalize(() => this.saving = false)).subscribe((res: any) => {
        this.updateAllUsersObservable();
        // create-route listens for the newly added driver / supervisor
        this.staffService.setStaffData(res, this.roleType);
        this.gradesService.setGradesReload();
        // A3: the account already works at another school, its name was updated everywhere
        if (res?.is_shared_staff === true) {
          this.showSharedStaffAdded(staffName);
        }
        this.staffID = res.id;
        this.saveLinks(true);
      },
        (error: HttpErrorResponse) => this.handleSaveError(error, !!nationalIdChoice));
    }
    else {
      if (this.oldRoleType !== this.roleType) {
        formData.append('new_role_type', this.roleType)
      }
      this.staffService.updateStaff(this.staffID, formData).pipe(finalize(() => this.saving = false)).subscribe(() => {
        if (this.clazz !== undefined) {
          this.gradesService.getSingleClass(this.clazz.id).subscribe(res => {
            this.gradesService.setGradesReload();
            this.gradesService.setClassData(res)
          })
        }
        this.updateAllUsersObservable();
        this.saveLinks(false);
      }, (error: HttpErrorResponse) => this.handleSaveError(error, false));
    }
  }

  // links are sent only when they changed, or the role changed (AC 11)
  private linksNeedSaving(afterAdd: boolean): boolean {
    if (!this.linkKind) {
      return false;
    }
    if (afterAdd) {
      return this.hasAnyLink;
    }
    if (this.roleType !== this.oldRoleType) {
      return true;
    }
    const current = this.currentLinks();
    return !sameIds(current.classIds, this.savedLinks.classIds)
      || !sameIds(current.gradeIds, this.savedLinks.gradeIds)
      || current.gateId !== this.savedLinks.gateId;
  }

  // the dialog closes only after both the person and their links have saved (AC 10)
  private saveLinks(afterAdd: boolean) {
    if (!this.linksNeedSaving(afterAdd)) {
      this.closeModalHandler();
      return;
    }
    this.saving = true;
    this.linksRequest().pipe(finalize(() => this.saving = false)).subscribe(() => {
      this.refreshLinkCaches();
      this.closeModalHandler();
    }, (error: HttpErrorResponse) => this.onLinksFailed(error, afterAdd));
  }

  // Retry resends only the links (AC 12)
  retryLinks() {
    this.linksError = null;
    this.saveLinks(false);
  }

  private linksRequest(): Observable<any> {
    const links = this.currentLinks();
    switch (this.linkKind) {
      case 'classes':
        return this.staffService.assignClassesToUser(this.staffID, { classes_ids: this.schoolClassIds(links.classIds) });
      case 'grades':
        return this.staffService.assignGradesToUser(this.staffID, { grades_ids: links.gradeIds });
      default:
        // null clears the gate
        return this.staffService.assignGateToUser(this.staffID, { gate_id: links.gateId });
    }
  }

  // the school route only takes this school's classes; a shared teacher's classes in other schools stay as they are.
  // Without the grades cache we can't tell them apart, so the ids go as they are.
  private schoolClassIds(classIds: number[]): number[] {
    const schoolClasses = new Set<number>();
    this.grades.forEach(grade => (grade.school_classes || []).forEach(clazz => schoolClasses.add(clazz.id)));
    return schoolClasses.size > 0 ? classIds.filter(id => schoolClasses.has(id)) : classIds;
  }

  private refreshLinkCaches() {
    if (this.linkKind === 'gate') {
      this.dataService.updateGatesCache();
    } else {
      this.dataService.updateGradesCache();
    }
  }

  // the person saved but the links did not: stay open in Edit mode (AC 12, 13)
  private onLinksFailed(error: HttpErrorResponse, afterAdd: boolean) {
    // HTTP 400 = links blocked by an active delegation: no Retry, the dialog can close
    this.linksError = error?.status === 400 ? 'blocked' : 'failed';
    if (!afterAdd) {
      return;
    }
    // after Add, reload the person from the server before switching to Edit mode (AC 14)
    const pending = this.currentLinks();
    this.saving = true;
    this.staffService.getStaffById(this.staffID).pipe(
      takeUntil(this.closed$),
      finalize(() => this.saving = false)
    ).subscribe((staff: any) => {
      this.formFlag = 'edit';
      this.setModalTitle();
      this.completedProfile = staff?.is_completed_profile === 1;
      this.staffName = staff?.name;
      this.oldRoleType = this.roleType;
      this.previousRole = this.roleType;
      this.savedLinks = this.linksFromStaff(staff, this.roleType);
      // keep what the admin picked, so Retry sends it
      this.applyLinks(pending);
    }, () => {
      this.formFlag = 'edit';
      this.setModalTitle();
      this.oldRoleType = this.roleType;
      this.previousRole = this.roleType;
    });
  }

  // Branches on error_type, never on the text (BLU-47 contract). Server messages come back in the request language.
  private handleSaveError(error: HttpErrorResponse, choiceAlreadySent: boolean) {
    const body = error?.error || {};
    // B1: same phone, other National ID, no staff role — ask which ID is correct, then resend
    if (error?.status === 409 && body.error_type === 'NATIONAL_ID_CHOICE_REQUIRED'
      && this.formFlag === 'add' && !choiceAlreadySent) {
      this.openNationalIdChoice(body.existing_national_id_last4);
      return;
    }
    // A2 never names a field, so it goes to the error popup
    if (body.error_type === 'STAFF_DETAILS_MISMATCH' || !body.errors) {
      this.errorMsgService.setMessage(body.message || this.translate.instant('add-staff.save-error'));
      return;
    }
    // field errors: B2 national_id, PHONE_IN_USE phone, A5 role_type / new_role_type,
    // SHARED_STAFF_DETAILS_LOCKED on the changed field, and the existing Laravel validation keys
    let shownInline = false;
    Object.keys(body.errors).forEach(key => {
      const controlName = key === 'new_role_type' ? 'role_type' : key;
      const control = this.addStaffForm.controls[controlName];
      if (!control) {
        return;
      }
      const messages = body.errors[key];
      const message = Array.isArray(messages) ? messages[0] : messages;
      control.setErrors({ incorrect: true, server: message || null });
      control.markAsTouched();
      shownInline = true;
    });
    if (!shownInline) {
      this.errorMsgService.setMessage(body.message || this.translate.instant('add-staff.save-error'));
    }
  }

  private openNationalIdChoice(existingLast4: string) {
    const countryCode = this.addStaffForm.value.country_code || '';
    const phone = this.addStaffForm.value.phone || '';
    this.nationalIdChoice = {
      existingLast4: existingLast4 || '',
      newNationalId: this.addStaffForm.value.national_id,
      phone: `${countryCode} ${phone}`.trim(),
      dir: this.currLang === 'ar' ? 'rtl' : 'ltr'
    };
  }

  onNationalIdChoiceClosed(choice: NationalIdChoice | null) {
    this.nationalIdChoice = null;
    if (choice) {
      this.submitStaff(choice);
    }
  }

  private showSharedStaffAdded(name: string) {
    this._snackBar.open(this.translate.instant('add-staff.shared-staff-added', { name }), '', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['copy-success-snackbar']
    });
  }

  updateAllUsersObservable(){
    this.dataService.getUsers$().subscribe(res=>{
      this.dataService.setUsers$(res);
    })
  }

  filterCafeteriaInRolesArray() {
    const coreRoles = ['school_admin', 'teacher', 'supervisor', 'security', 'shared_gate_scanner', 'gate', 'manager'];
    const includeCafeteria = this.formFlag == 'add' || this.oldRoleType == 'cafeteria';
    const allRoles = includeCafeteria ? [...coreRoles, 'cafeteria'] : coreRoles;
    // keep School Admin in the list when viewing an existing admin, otherwise the select has no option to show their role
    const viewingExistingAdmin = this.formFlag !== 'add' && this.oldRoleType === Role.SchoolAdmin;
    this.roles = allRoles.filter(role => viewingExistingAdmin || canManageStaffRole(this.loggedInRole, role));
  }

  ngOnDestroy() {
    this.closed$.next();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
